import type { UserActivityAction } from '@ai-meeting/shared'
import type { SessionKindPanelLayout } from '@/constants/session-kind'

/**
 * Which action records each of the live screen's five panels (BRAI-677).
 *
 * Typed `Record<keyof SessionKindPanelLayout, …>`, so a sixth panel added to
 * the layout stops the build here rather than quietly going uncounted.
 */
const PANEL_IN_USE: Record<keyof SessionKindPanelLayout, UserActivityAction> = {
  sourceOpen: 'panel.input_in_use',
  targetOpen: 'panel.output_in_use',
  batchOpen: 'panel.enhanced_in_use',
  memoOpen: 'panel.live_memo_in_use',
  keyPointsOpen: 'panel.key_points_in_use',
}

/**
 * The panels a reader still had open when they ended the meeting.
 *
 * Read once, at the end, instead of counting clicks on the fold controls.
 * Which panels start open is decided by the Mode (`SESSION_KIND_PANEL_LAYOUT`)
 * rather than by the reader, so a click counter would report zero uses of
 * Input — the pane almost everybody reads and almost nobody touches. Counting
 * the state means a panel left at its default counts exactly like one somebody
 * went and opened, which is what "which panels do people work with" asks.
 *
 * It does not see a panel opened and shut again mid-meeting, and it only
 * happens when End is pressed — closing the tab records nothing, the same
 * coverage `meeting.ended` already has.
 *
 * @returns One action per open panel, in layout order; empty when the reader
 *   had everything folded, which is the whole live screen for an internal
 *   meeting.
 */
export function openPanelActivities(layout: SessionKindPanelLayout): UserActivityAction[] {
  const panels = Object.keys(PANEL_IN_USE) as (keyof SessionKindPanelLayout)[]
  return panels.filter((panel) => layout[panel]).map((panel) => PANEL_IN_USE[panel])
}
